import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Cpu, Database, Layers, Gauge, ShieldCheck, Settings2, LayoutDashboard,
  Camera, Thermometer, Tractor, Truck, Factory, MonitorSmartphone, Radio, SlidersHorizontal,
  CheckCircle2,
} from "lucide-react";
import SEO from "@/components/SEO";
import { usePlans } from "@/hooks/usePlans";

const MODES = [
  { icon: <Cpu size={22} />, title: "IoT SIMs — per device", text: "Each device gets its own SIM or eSIM with its own data allocation. Simple to manage, simple to bill, ideal when every device has a predictable, similar usage pattern." },
  { icon: <Database size={22} />, title: "Data Pools — shared across many", text: "One shared data allowance split across every SIM in the pool. Built for fleets with uneven usage — some devices barely transmit, others send bursts of data — so nothing goes to waste." },
];

const USE_CASES = [
  { icon: <Camera size={18} />, label: "Remote CCTV & security monitoring" },
  { icon: <Thermometer size={18} />, label: "Environmental & industrial sensors" },
  { icon: <Tractor size={18} />, label: "Agriculture & irrigation telemetry" },
  { icon: <Truck size={18} />, label: "Fleet tracking & telematics" },
  { icon: <Factory size={18} />, label: "SCADA & industrial infrastructure" },
  { icon: <MonitorSmartphone size={18} />, label: "Digital signage & smart kiosks" },
  { icon: <Radio size={18} />, label: "EFTPOS, ATMs & backup connectivity" },
];

const FEATURES = [
  { icon: <LayoutDashboard size={20} />, title: "Centralise", text: "Every SIM, every device, every data allowance — one Connectivity Portal login, one place to see it all, instead of juggling multiple carrier logins." },
  { icon: <Gauge size={20} />, title: "Streamline", text: "One consolidated invoice across the whole fleet, whether it's 5 SIMs or 5,000 — no per-carrier billing reconciliation." },
  { icon: <SlidersHorizontal size={20} />, title: "Scale", text: "Add or remove SIMs, resize a pool, or move a device between plans — all over the air, all without a truck roll or a call centre queue." },
  { icon: <ShieldCheck size={20} />, title: "Stay connected", text: "Every IoT SIM and Data Pool inherits PTT NZ's multi-network foundation — automatic failover across Spark NZ, One NZ, 2degrees, plus native access to Telstra, Optus & Vodafone in Australia." },
];

const IotDataPools = () => {
  const { loading, plansByCategory } = usePlans();
  const iotPlans = plansByCategory.iot || [];
  const dataPoolPlans = plansByCategory["data-pools"] || [];
  const allPrices = [...iotPlans, ...dataPoolPlans].filter((p) => p.price != null).map((p) => p.price);
  const cheapest = allPrices.length ? Math.min(...allPrices) : null;

  return (
    <div data-testid="iot-data-pools-page">
      <SEO
        path="/iot-data-pools"
        title="Cheapest Cellular IoT & Data Pools NZ & AU"
        description="Cheapest cellular IoT in NZ from $2.95/month (from $2.36 AUD in Australia), no contract. Multi-network Data Pools for device monitoring & infrastructure."
        keywords="cheapest cellular IoT NZ, cheapest IoT SIM New Zealand, multi-network data pools NZ, wholesale MVNO provider, flexible M2M data, IoT data pool New Zealand, IoT SIM plans NZ, IoT SIM plans Australia, device monitoring SIM plans, IoT connectivity infrastructure NZ, M2M SIM data pool, multi-network IoT SIM, scalable IoT SIM management, IoT fleet SIM management NZ, shared data pool SIM plans, remote sensor SIM data plan, industrial IoT connectivity NZ, government IoT connectivity NZ, enterprise fleet IoT AU, open term no contract SIM NZ"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: "IoT SIM & Data Pool connectivity management",
            provider: { "@type": "Organization", name: "PTT NZ Limited", url: "https://pttnz.net" },
            areaServed: ["New Zealand", "Australia"],
            name: "PTT NZ IoT SIMs & Data Pools",
            description: "Multi-network IoT SIMs and shared Data Pools for device monitoring and infrastructure, centrally managed from the PTT NZ Connectivity Portal.",
          },
          cheapest != null ? {
            "@context": "https://schema.org",
            "@type": "Product",
            name: "PTT NZ Cellular IoT SIM & Data Pool",
            description: "Wholesale-grade multi-network cellular IoT SIM and Data Pool, open term, no contract, connecting Spark NZ, 2degrees, One NZ, Telstra, Vodafone and Optus.",
            brand: { "@type": "Organization", name: "PTT NZ Limited" },
          offers: [
              {
                "@type": "AggregateOffer",
                priceCurrency: "NZD",
                lowPrice: cheapest.toFixed(2),
                offerCount: iotPlans.length + dataPoolPlans.length,
                url: "https://pttnz.net/iot-data-pools",
                availability: "https://schema.org/InStock",
              },
              {
                "@type": "Offer",
                priceCurrency: "AUD",
                price: "2.36",
                name: "Entry Cellular IoT SIM — Australia, 100MB",
                url: "https://pttnz.net/iot-data-pools",
                availability: "https://schema.org/InStock",
              },
            ],
          } : null,
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              { "@type": "Question", name: "What is the cheapest cellular IoT plan in New Zealand?", acceptedAnswer: { "@type": "Answer", text: "PTT NZ's entry cellular IoT plan starts from $2.95 NZD/month for 100MB, open term with no contract, connecting to all three New Zealand networks and all three Australian networks on one multi-network SIM." } },
              { "@type": "Question", name: "What is the cheapest cellular IoT plan in Australia?", acceptedAnswer: { "@type": "Answer", text: "PTT NZ's entry cellular IoT plan in Australia starts from $2.36 AUD/month for 100MB, open term with no contract, on the same multi-network SIM that connects across Spark NZ, One NZ and 2degrees plus native access to Telstra, Optus and Vodafone." } },
              { "@type": "Question", name: "What's the difference between an IoT SIM and a Data Pool?", acceptedAnswer: { "@type": "Answer", text: "An IoT SIM gives one device its own individual data allocation. A Data Pool shares one combined data allowance across many SIMs at once, which suits fleets where usage varies device to device." } },
              { "@type": "Question", name: "Is PTT NZ a wholesale MVNO provider?", acceptedAnswer: { "@type": "Answer", text: "Yes. PTT NZ operates as a full multi-network MVNO across New Zealand and Australia, offering wholesale white-label access to its SIM and data pool platform for other businesses to build on." } },
            ],
          },
        ].filter(Boolean)}
      />

      {/* HERO */}
      <section className="bg-[var(--ptt-paper)] border-b border-[var(--ptt-line)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-16 pb-14">
          <div className="eyebrow">Wholesale-grade IoT &amp; Data Pools</div>
          <h1 className="font-display text-4xl lg:text-6xl font-bold mt-3 leading-[1.02] tracking-tight max-w-4xl">
            Scalable IoT Data Pools built for modern <span className="text-[var(--ptt-teal-deep)]">device monitoring &amp; infrastructure.</span>
          </h1>
          <p className="text-[var(--ptt-muted)] mt-5 max-w-2xl text-lg">
            Centralise, streamline and scale with intelligent multi-network connectivity for every sensor, camera, tracker and controller in your fleet — flexible M2M data, open term, no contract.
          </p>

          {/* LIVE PRICING HERO */}
          <div className="mt-9 border border-[var(--ptt-ink)] bg-white p-6 lg:p-7 flex flex-wrap items-center gap-6 max-w-2xl" data-testid="iot-live-pricing-hero">
            <div className="shrink-0">
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)]">From</div>
              {loading ? (
                <div className="h-12 w-28 bg-[var(--ptt-line)] animate-pulse mt-1" />
              ) : (
                <div className="font-display text-5xl font-bold leading-none mt-1" data-testid="iot-live-price">
                  ${cheapest != null ? cheapest.toFixed(2) : "—"}<span className="text-sm font-normal text-[var(--ptt-muted)]">/mo</span>
                </div>
              )}
              <div className="text-xs text-[var(--ptt-muted)] mt-1">100MB · updated live from the Portal</div>
              <div className="text-xs text-[var(--ptt-muted)] mt-1">AU: from $2.36 AUD/mo for 100MB</div>
            </div>
            <div className="flex flex-wrap gap-2 flex-1">
              {["Open term", "No contract", "Tri-network NZ", "Tri-network AU"].map((b) => (
                <span key={b} className="font-mono-tech text-[10px] uppercase tracking-widest px-2.5 py-1.5 border border-[var(--ptt-line)] text-[var(--ptt-muted)] inline-flex items-center gap-1.5"><CheckCircle2 size={11} className="text-[var(--ptt-teal-deep)]" />{b}</span>
              ))}
            </div>
          </div>

          <div className="mt-7 flex gap-3 flex-wrap">
            <Link to="/plans#iot" data-testid="iot-hero-cta-plans" className="ptt-button px-5 py-3 text-sm font-bold inline-flex items-center gap-2">See all live IoT &amp; Data Pool pricing <ArrowRight size={14} /></Link>
            <Link to="/contact?interest=IoT%20Plans" data-testid="iot-hero-cta-contact" className="ghost-button px-5 py-3 text-sm font-semibold inline-flex items-center gap-2">Talk to us</Link>
          </div>
        </div>
      </section>

    {/* TWO MODES */}
    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
      <div className="eyebrow">One platform, two ways to allocate data</div>
      <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05] max-w-2xl">Per-SIM IoT plans, or a shared Data Pool — same multi-network foundation underneath.</h2>
      <div className="grid sm:grid-cols-2 gap-5 mt-10" data-testid="iot-modes-grid">
        {MODES.map((m) => (
          <div key={m.title} className="p-7 bg-white border border-[var(--ptt-line)] hover:border-[var(--ptt-ink)] transition-colors">
            <div className="w-11 h-11 grid place-items-center bg-[var(--ptt-teal)]/15">{m.icon}</div>
            <h3 className="font-display text-lg font-bold mt-5">{m.title}</h3>
            <p className="text-sm text-[var(--ptt-muted)] mt-2 leading-relaxed">{m.text}</p>
          </div>
        ))}
      </div>
    </section>

    {/* CENTRALISE STREAMLINE SCALE */}
    <section className="bg-[var(--ptt-ink)] text-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
        <div className="eyebrow text-[var(--ptt-teal)]">Why teams choose PTT NZ</div>
        <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05] max-w-2xl">Centralise, streamline, and scale with intelligent data pools for IoT devices.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10" data-testid="iot-features-grid">
          {FEATURES.map((f) => (
            <div key={f.title} className="border border-white/15 p-6">
              <div className="w-10 h-10 grid place-items-center bg-white/10 text-[var(--ptt-teal)]">{f.icon}</div>
              <h3 className="font-display text-base font-bold mt-4">{f.title}</h3>
              <p className="text-sm text-white/65 mt-2 leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* USE CASES */}
    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
      <div className="eyebrow">Built for</div>
      <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05] max-w-2xl">Device monitoring &amp; infrastructure, at any scale.</h2>
      <div className="flex flex-wrap gap-3 mt-8" data-testid="iot-use-cases">
        {USE_CASES.map((u) => (
          <div key={u.label} className="flex items-center gap-2.5 bg-white border border-[var(--ptt-line)] px-4 py-2.5 text-sm font-medium">
            <span className="text-[var(--ptt-teal-deep)]">{u.icon}</span>{u.label}
          </div>
        ))}
      </div>
    </section>

    {/* MORE OPTIONS */}
    <section className="bg-[var(--ptt-paper)] border-y border-[var(--ptt-line)]">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
        <div className="eyebrow">Need more control?</div>
        <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05] max-w-2xl">Pair your IoT fleet with our other connectivity tools.</h2>
        <div className="grid sm:grid-cols-3 gap-5 mt-8">
          <Link to="/solutions/private-apn" data-testid="iot-related-private-apn" className="p-6 bg-white border border-[var(--ptt-line)] hover:border-[var(--ptt-ink)] transition-colors group">
            <Settings2 size={20} className="text-[var(--ptt-teal-deep)]" />
            <div className="font-display text-base font-bold mt-4">Private APN &amp; Secure VPN</div>
            <p className="text-sm text-[var(--ptt-muted)] mt-2">Keep fleet traffic off the public internet entirely, with static IPs where needed.</p>
            <div className="mt-4 text-sm font-semibold signal-underline inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">Learn more <ArrowRight size={13} /></div>
          </Link>
          <Link to="/solutions/byoc" data-testid="iot-related-byoc" className="p-6 bg-white border border-[var(--ptt-line)] hover:border-[var(--ptt-ink)] transition-colors group">
            <Layers size={20} className="text-[var(--ptt-teal-deep)]" />
            <div className="font-display text-base font-bold mt-4">BYOC Smart SIM</div>
            <p className="text-sm text-[var(--ptt-muted)] mt-2">Keep an existing carrier profile active, with PTT NZ as automatic OTA failover.</p>
            <div className="mt-4 text-sm font-semibold signal-underline inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">Learn more <ArrowRight size={13} /></div>
          </Link>
          <Link to="/plans#data-pools" data-testid="iot-related-plans" className="p-6 bg-white border border-[var(--ptt-line)] hover:border-[var(--ptt-ink)] transition-colors group">
            <Database size={20} className="text-[var(--ptt-teal-deep)]" />
            <div className="font-display text-base font-bold mt-4">See all IoT &amp; Data Pool pricing</div>
            <p className="text-sm text-[var(--ptt-muted)] mt-2">Every live plan and price, alongside every other PTT NZ plan, on one page.</p>
            <div className="mt-4 text-sm font-semibold signal-underline inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">View Plans <ArrowRight size={13} /></div>
          </Link>
        </div>
      </div>
    </section>

    {/* FINAL CTA */}
    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20 mb-8">
      <div className="relative border border-[var(--ptt-ink)] p-8 lg:p-14 bg-white overflow-hidden">
        <div className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-[var(--ptt-teal)]/20 blur-3xl" />
        <div className="relative grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="eyebrow">Talk to us</div>
            <h3 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-tight">Tell us what you're monitoring — we'll size the pool.</h3>
            <p className="text-[var(--ptt-muted)] mt-4 max-w-2xl">From a handful of sensors to a nationwide fleet, we'll help you pick per-SIM or pooled data, and get every device online.</p>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
            <Link to="/contact?interest=IoT%20Plans" data-testid="iot-final-cta-contact" className="ptt-button px-6 py-3.5 text-sm font-bold inline-flex items-center gap-2 w-full sm:w-auto justify-center">Talk to us <ArrowRight size={16} /></Link>
            <Link to="/plans#iot" data-testid="iot-final-cta-plans" className="ghost-button px-6 py-3.5 text-sm font-semibold inline-flex items-center gap-2 w-full sm:w-auto justify-center">See pricing on Plans</Link>
          </div>
        </div>
      </div>
    </section>
    </div>
  );
};

export default IotDataPools;
